// src/app/components/AddNewDocument.tsx

import {
  AudioOutlined,
  CheckOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  StarOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Modal,
  Progress,
  Row,
  Select,
  Spin,
  Statistic,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { UploadChangeParam } from "antd/es/upload";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpenCheck,
  BookOpenText,
  Brain,
  CheckSquare,
  FileText,
  GraduationCap,
  Layers,
  Repeat,
} from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { notify } from "@/components/Notification";
import ResetButton from "@/components/ResetButton";
import { MAX_DOCUMENT_SIZE_BYTES, MAX_DOCUMENT_SIZE_MB, MINUTES_PER_CREDIT } from "@/lib/constants";
import useAppStore from "@/store/useAppStore";
import type { CostBreakdown, DocumentInfo } from "@/types";
import { safeStringify } from "@/utils/api";
import { calculateCostBreakdown } from "@/utils/documentUtils";
import { trackEvent } from "@/utils/gtm";
import AudienceSelector from "./AudienceSelector";

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

/**
 * Supported MIME types for audio and video files.
 * These sets are used to determine if a file is audio/video and to display the appropriate icon.
 */
const AUDIO_MIME_TYPES = new Set([
  "audio/mpeg",
  "audio/wav",
  "audio/wave",
  "audio/x-wav",
  "audio/aac",
  "audio/ogg",
  "audio/webm",
  "audio/x-m4a",
  "audio/mp4",
  "audio/x-ms-wma",
  "audio/flac",
  "audio/x-flac",
]);

const VIDEO_MIME_TYPES = new Set([
  "video/mp4",
  "video/x-matroska",
  "video/x-msvideo",
  "video/quicktime",
  "video/x-ms-wmv",
  "video/webm",
]);

/**
 * Determines if a given MIME type represents an audio or video file.
 * Used to adjust icon displays and some tracking events.
 */
function isAudioOrVideoFile(mimeType: string): boolean {
  return (
    AUDIO_MIME_TYPES.has(mimeType.toLowerCase()) || VIDEO_MIME_TYPES.has(mimeType.toLowerCase())
  );
}

/**
 * Main component for handling new document uploads and configuring what content
 * to generate (quizzes, summary, flashcards, slides, etc.). Interacts with a global store (Zustand)
 * to maintain state.
 */
const AddNewDocument: React.FC = () => {
  // Extracting state and actions from the global app store
  const {
    documentFile,
    documentInfo,
    generateMultipleChoiceQuiz,
    generateShortAnswerQuiz,
    generateAnkiFlashcards,
    generatePresentationSlides,
    generateSummary,
    generateMindmap,
    generateReadabilityAnalysis,
    generateLessonPlan,
    lessonPlanTargetAudience,
    presentationTheme,
    costBreakdown,
    isProcessingDocument,
    error,
    setDocumentFile,
    setGenerateMultipleChoiceQuiz,
    setGenerateShortAnswerQuiz,
    setGenerateAnkiFlashcards,
    setGeneratePresentationSlides,
    setGenerateSummary,
    setGenerateMindmap,
    setGenerateReadabilityAnalysis,
    setGenerateLessonPlan,
    setLessonPlanTargetAudience,
    setPresentationTheme,
    handleSubmit,
    handleDocumentChange,
    setError,
    resetJobState,
    resetDocumentState,
    currentJobId,
  } = useAppStore();

  const [isLoadingDocumentInfo, setIsLoadingDocumentInfo] = useState(false);
  const [fileType, setFileType] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [customLessonPlanTargetAudience, setCustomLessonPlanTargetAudience] = useState("");
  // Tracks the "Process File" button click state locally.

  /**
   * Predefined presentation themes for slides. Each theme has a label and a sample image.
   * User can choose one to apply to the generated slides.
   */
  const presentationThemes = useMemo(
    () => [
      { name: "black", label: "Black", imgSrc: "/presentation_theme_sample_images/black.png" },
      { name: "white", label: "White", imgSrc: "/presentation_theme_sample_images/white.png" },
      { name: "league", label: "League", imgSrc: "/presentation_theme_sample_images/league.png" },
      { name: "beige", label: "Beige", imgSrc: "/presentation_theme_sample_images/beige.png" },
      { name: "night", label: "Night", imgSrc: "/presentation_theme_sample_images/night.png" },
      { name: "moon", label: "Moon", imgSrc: "/presentation_theme_sample_images/moon.png" },
      { name: "serif", label: "Serif", imgSrc: "/presentation_theme_sample_images/serif.png" },
      { name: "simple", label: "Simple", imgSrc: "/presentation_theme_sample_images/simple.png" },
      { name: "sky", label: "Sky", imgSrc: "/presentation_theme_sample_images/sky.png" },
      {
        name: "solarized",
        label: "Solarized",
        imgSrc: "/presentation_theme_sample_images/solarized.png",
      },
      {
        name: "dracula",
        label: "Dracula",
        imgSrc: "/presentation_theme_sample_images/dracula.png",
      },
      { name: "blood", label: "Blood", imgSrc: "/presentation_theme_sample_images/blood.png" },
    ],
    [],
  );

  /**
   * Handle file changes from the upload component (Dragger).
   * When a file is selected, this function uploads it to get preliminary document info
   * (such as word count, total pages, and cost breakdown).
   */
  const handleFileChange = useCallback(
    async (info: UploadChangeParam) => {
      const file = info.fileList?.[0]?.originFileObj;
      if (!file) {
        return;
      }

      if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
        const errorMessage = `File too large. Max size is ${MAX_DOCUMENT_SIZE_MB.toFixed(0)} MB.`;
        notify.error({ content: errorMessage });
        setError(errorMessage);
        setDocumentFile(null);
        setIsLoadingDocumentInfo(false);
        setUploadProgress(0);
        return;
      }

      setDocumentFile(file);
      setIsLoadingDocumentInfo(true);
      setFileType(file.type);
      setUploadProgress(0);

      try {
        trackEvent("document_upload_started", {
          file_type: file.type,
          file_size: file.size,
          is_audio_video: isAudioOrVideoFile(file.type),
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("generateMultipleChoiceQuiz", String(generateMultipleChoiceQuiz));
        formData.append("generateShortAnswerQuiz", String(generateShortAnswerQuiz));
        formData.append("generateAnkiFlashcards", String(generateAnkiFlashcards));
        formData.append("generatePresentationSlides", String(generatePresentationSlides));
        formData.append("generateSummary", String(generateSummary));
        formData.append("generateMindmap", String(generateMindmap));
        formData.append("generateReadabilityAnalysis", String(generateReadabilityAnalysis));
        formData.append("generateLessonPlan", String(generateLessonPlan));
        formData.append("lessonPlanTargetAudience", lessonPlanTargetAudience);
        formData.append("presentationTheme", presentationTheme);

        const response = await axios.post<DocumentInfo>("/api/get_document_info", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        });

        if (response.status !== 200) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const documentData: DocumentInfo = response.data;
        useAppStore.setState((state) => ({
          ...state,
          documentInfo: documentData,
          costBreakdown: documentData.costBreakdown,
        }));

        trackEvent("document_upload_success", {
          file_type: file.type,
          file_size: file.size,
          pages: documentData.totalPages,
          is_audio_video: isAudioOrVideoFile(file.type),
        });
      } catch (error) {
        console.error("Error handling document upload:", safeStringify(error));
        let errorMessage = "Failed to process document";

        if (axios.isAxiosError(error)) {
          const errorDetail = error.response?.data?.details
            ? JSON.stringify(error.response.data.details)
            : error.response?.data?.error || error.message;

          errorMessage = `Error processing document: ${errorDetail}`;
          console.error("Detailed error information:", {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
        }

        trackEvent("document_upload_error", {
          file_type: fileType || "unknown",
          error: errorMessage,
          is_audio_video: isAudioOrVideoFile(file?.type || ""),
        });

        setError(errorMessage);
        notify.error({ content: errorMessage });
        setDocumentFile(null);
      } finally {
        setIsLoadingDocumentInfo(false);
        setUploadProgress(0);
      }
    },
    [
      generateMultipleChoiceQuiz,
      generateShortAnswerQuiz,
      generateAnkiFlashcards,
      generatePresentationSlides,
      generateSummary,
      generateMindmap,
      generateReadabilityAnalysis,
      generateLessonPlan,
      lessonPlanTargetAudience,
      presentationTheme,
      setDocumentFile,
      setError,
      fileType,
    ],
  );

  /**
   * Get the appropriate file-type icon based on the MIME type of the uploaded file.
   * This helps users visually identify the type of document they've uploaded.
   */
  const getFileTypeIcon = useCallback((fileType: string | null) => {
    const iconProps = { style: { fontSize: "48px" } };
    if (!fileType) return <FileUnknownOutlined style={{ ...iconProps.style, color: "#8c8c8c" }} />;

    // Audio/Video icons have priority
    if (AUDIO_MIME_TYPES.has(fileType.toLowerCase())) {
      return <AudioOutlined style={{ ...iconProps.style, color: "#722ed1" }} />;
    }
    if (VIDEO_MIME_TYPES.has(fileType.toLowerCase())) {
      return <VideoCameraOutlined style={{ ...iconProps.style, color: "#eb2f96" }} />;
    }

    // Else, pick an icon based on known document MIME types
    switch (fileType) {
      case "application/pdf":
        return <FilePdfOutlined style={{ ...iconProps.style, color: "#ff4d4f" }} />;
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return <FileWordOutlined style={{ ...iconProps.style, color: "#1890ff" }} />;
      case "application/vnd.ms-excel":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        return <FileExcelOutlined style={{ ...iconProps.style, color: "#52c41a" }} />;
      case "application/vnd.ms-powerpoint":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        return <FilePptOutlined style={{ ...iconProps.style, color: "#fa8c16" }} />;
      case "image/jpeg":
      case "image/png":
      case "image/gif":
        return <FileImageOutlined style={{ ...iconProps.style, color: "#13c2c2" }} />;
      default:
        return <FileUnknownOutlined style={{ ...iconProps.style, color: "#8c8c8c" }} />;
    }
  }, []);

  /**
   * Update generation options (e.g., multiple choice quiz, flashcards, etc.) when a checkbox changes.
   * Also re-calculates the cost breakdown if the document info is available.
   */
  const handleOptionChange = useCallback(
    (option: string, checked: boolean) => {
      switch (option) {
        case "multipleChoice":
          setGenerateMultipleChoiceQuiz(checked);
          break;
        case "shortAnswer":
          setGenerateShortAnswerQuiz(checked);
          break;
        case "ankiFlashcards":
          setGenerateAnkiFlashcards(checked);
          break;
        case "presentationSlides":
          setGeneratePresentationSlides(checked);
          break;
        case "summary":
          setGenerateSummary(checked);
          break;
        case "mindmap":
          setGenerateMindmap(checked);
          break;
        case "readabilityAnalysis":
          setGenerateReadabilityAnalysis(checked);
          break;
        case "lessonPlan":
          setGenerateLessonPlan(checked);
          break;
      }

      // If we have wordCount, recalculate cost breakdown for updated options
      if (documentInfo?.wordCount) {
        const newOptions = {
          generateMultipleChoiceQuiz:
            option === "multipleChoice" ? checked : generateMultipleChoiceQuiz,
          generateShortAnswerQuiz: option === "shortAnswer" ? checked : generateShortAnswerQuiz,
          generateAnkiFlashcards: option === "ankiFlashcards" ? checked : generateAnkiFlashcards,
          generatePresentationSlides:
            option === "presentationSlides" ? checked : generatePresentationSlides,
          generateSummary: option === "summary" ? checked : generateSummary,
          generateMindmap: option === "mindmap" ? checked : generateMindmap,
          generateReadabilityAnalysis:
            option === "readabilityAnalysis" ? checked : generateReadabilityAnalysis,
          generateLessonPlan: option === "lessonPlan" ? checked : generateLessonPlan,
        };

        const newCostBreakdown = calculateCostBreakdown(documentInfo.wordCount, newOptions);

        useAppStore.setState((state) => ({
          ...state,
          costBreakdown: newCostBreakdown,
          documentInfo: state.documentInfo
            ? {
                ...state.documentInfo,
                costBreakdown: newCostBreakdown,
              }
            : null,
        }));
      }

      trackEvent("option_change", {
        option,
        enabled: checked,
      });
    },
    [
      documentInfo?.wordCount,
      setGenerateMultipleChoiceQuiz,
      setGenerateShortAnswerQuiz,
      setGenerateAnkiFlashcards,
      setGeneratePresentationSlides,
      setGenerateSummary,
      setGenerateMindmap,
      setGenerateReadabilityAnalysis,
      setGenerateLessonPlan,
      generateMultipleChoiceQuiz,
      generateShortAnswerQuiz,
      generateAnkiFlashcards,
      generatePresentationSlides,
      generateSummary,
      generateMindmap,
      generateReadabilityAnalysis,
      generateLessonPlan,
    ],
  );

  /**
   * Wrapper for submitting the document processing request.
   * This triggers actual processing on the backend with the selected options.
   */
  const handleSubmitWrapper = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (!documentFile) return;

      trackEvent("process_document_clicked", {
        file_type: documentFile.type,
        file_size: documentFile.size,
        is_audio_video: isAudioOrVideoFile(documentFile.type),
        multiple_choice_quiz: generateMultipleChoiceQuiz,
        short_answer_quiz: generateShortAnswerQuiz,
        anki_flashcards: generateAnkiFlashcards,
        presentation_slides: generatePresentationSlides,
        summary: generateSummary,
        mindmap: generateMindmap,
        readability_analysis: generateReadabilityAnalysis,
        lesson_plan: generateLessonPlan,
      });

      handleSubmit(documentFile, {
        generateMultipleChoiceQuiz,
        generateShortAnswerQuiz,
        generateAnkiFlashcards,
        generatePresentationSlides,
        generateSummary,
        generateMindmap,
        generateReadabilityAnalysis,
        generateLessonPlan,
        lessonPlanTargetAudience,
        presentationTheme,
      });
    },
    [
      documentFile,
      generateMultipleChoiceQuiz,
      generateShortAnswerQuiz,
      generateAnkiFlashcards,
      generatePresentationSlides,
      generateSummary,
      generateMindmap,
      generateReadabilityAnalysis,
      generateLessonPlan,
      lessonPlanTargetAudience,
      presentationTheme,
      handleSubmit,
    ],
  );

  /**
   * Resets all current processing states and uploaded documents.
   * Opens a confirmation modal before actually resetting.
   */
  const handleResetAll = useCallback(() => {
    Modal.confirm({
      title: "Are you sure you want to reset?",
      content: "This will clear all current processing states and uploaded documents.",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        if (currentJobId) {
          try {
            await axios.post("/api/clear-job", { jobId: currentJobId });
          } catch (error) {
            console.error("Error clearing job:", safeStringify(error));
          }
        }
        resetJobState();
        trackEvent("processing_state_reset", {});
      },
    });
  }, [currentJobId, resetJobState]);

  /**
   * Handles removing the currently uploaded file and resetting all related states/options.
   */
  const handleRemoveFile = useCallback(() => {
    resetJobState();

    // Reset local states
    setDocumentFile(null);
    setFileType(null);
    setUploadProgress(0);
    setError(null);

    // Reset all generation options
    setGenerateMultipleChoiceQuiz(false);
    setGenerateShortAnswerQuiz(false);
    setGenerateAnkiFlashcards(false);
    setGeneratePresentationSlides(false);
    setGenerateSummary(false);
    setGenerateMindmap(false);
    setGenerateReadabilityAnalysis(false);
    setGenerateLessonPlan(false);
    // Reset target audience and theme to defaults
    setLessonPlanTargetAudience(
      "American high school students between the ages of 15 and 18 with average scholastic aptitude",
    );
    setPresentationTheme("black");

    trackEvent("document_removed", {
      file_type: fileType || "unknown",
      is_audio_video: isAudioOrVideoFile(fileType || ""),
    });
  }, [
    resetJobState,
    setDocumentFile,
    setError,
    setGenerateMultipleChoiceQuiz,
    setGenerateShortAnswerQuiz,
    setGenerateAnkiFlashcards,
    setGeneratePresentationSlides,
    setGenerateSummary,
    setGenerateMindmap,
    setGenerateReadabilityAnalysis,
    setGenerateLessonPlan,
    setLessonPlanTargetAudience,
    setPresentationTheme,
    fileType,
  ]);

  /**
   * Renders the cost breakdown section as a series of progress bars showing how each selected option contributes to the total cost.
   */
  const renderCostBreakdown = useCallback(
    (breakdown: CostBreakdown) => {
      const estimatedMinutes = Math.max(0, Math.round(breakdown.totalCost * MINUTES_PER_CREDIT));
      const estimatedTimeLabel =
        estimatedMinutes >= 90
          ? `${(estimatedMinutes / 60).toFixed(1)} hr`
          : `${estimatedMinutes} min`;

      const lineItems: Array<{
        label: string;
        credits: number;
        enabled: boolean;
        subtle?: boolean;
      }> = [
        { label: "Base processing", credits: breakdown.baseCost, enabled: breakdown.baseCost > 0 },
        {
          label: "Multiple choice quiz",
          credits: breakdown.multipleChoiceCost,
          enabled: generateMultipleChoiceQuiz && breakdown.multipleChoiceCost > 0,
        },
        {
          label: "Short answer quiz",
          credits: breakdown.shortAnswerCost,
          enabled: generateShortAnswerQuiz && breakdown.shortAnswerCost > 0,
        },
        {
          label: "Anki flashcards",
          credits: breakdown.ankiFlashcardsCost,
          enabled: generateAnkiFlashcards && breakdown.ankiFlashcardsCost > 0,
        },
        {
          label: "Presentation slides",
          credits: breakdown.presentationSlidesCost,
          enabled: generatePresentationSlides && breakdown.presentationSlidesCost > 0,
        },
        {
          label: "Summary",
          credits: breakdown.summaryCost,
          enabled: generateSummary && breakdown.summaryCost > 0,
        },
        {
          label: "Mindmap",
          credits: breakdown.mindmapCost,
          enabled: generateMindmap && breakdown.mindmapCost > 0,
        },
        {
          label: "Readability analysis",
          credits: breakdown.readabilityAnalysisCost,
          enabled: generateReadabilityAnalysis && breakdown.readabilityAnalysisCost > 0,
        },
        {
          label: "Lesson plan",
          credits: breakdown.lessonPlanCost,
          enabled: generateLessonPlan && breakdown.lessonPlanCost > 0,
        },
        {
          label: "Bundle discount",
          credits: -Math.abs(breakdown.quizDiscount),
          enabled:
            generateMultipleChoiceQuiz && generateShortAnswerQuiz && breakdown.quizDiscount > 0,
          subtle: true,
        },
      ];

      return (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <Title level={5} className="mb-0 flex items-center text-gray-800">
              Cost
              <Tooltip title="Credits are spent when you confirm processing." mouseEnterDelay={0.5}>
                <QuestionCircleOutlined className="ml-2 text-gray-400 hover:text-indigo-500 transition-colors" />
              </Tooltip>
            </Title>
            {breakdown.totalCost > 0 && <Text type="secondary">Est. {estimatedTimeLabel}</Text>}
          </div>

          <div className="mt-3 space-y-2">
            {lineItems
              .filter((item) => item.enabled && item.credits !== 0)
              .map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <Text type={item.subtle ? "secondary" : undefined}>{item.label}</Text>
                  <Text type={item.subtle ? "secondary" : undefined}>
                    {item.credits < 0 ? `-${Math.abs(item.credits)}` : item.credits} credits
                  </Text>
                </div>
              ))}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <Text strong>Total</Text>
            <Text strong>{breakdown.totalCost} credits</Text>
          </div>
        </div>
      );
    },
    [
      generateMultipleChoiceQuiz,
      generateShortAnswerQuiz,
      generateAnkiFlashcards,
      generatePresentationSlides,
      generateSummary,
      generateMindmap,
      generateReadabilityAnalysis,
      generateLessonPlan,
    ],
  );

  /**
   * Displays the uploaded document's information (filename, pages, word count, etc.)
   * along with the cost breakdown and a button to remove the document.
   */
  const renderDocumentInfo = useCallback(
    (info: DocumentInfo) => (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="shadow-lg rounded-lg overflow-hidden mb-2 bg-gradient-to-r from-blue-50 to-indigo-50 hover:shadow-xl transition-shadow relative group">
          <Tooltip title="Remove document?" mouseEnterDelay={0.5}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveFile}
              disabled={isProcessingDocument}
              className="absolute top-4 right-4 transition-opacity hover:scale-110 z-10"
              size="large"
              aria-label="Remove Document"
            />
          </Tooltip>
          <Row gutter={16} align="middle">
            <Col xs={12} md={8} className="mb-4 md:mb-0">
              <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-md">
                {getFileTypeIcon(fileType)}
                <Text strong className="mt-2 text-center">
                  {info.fileName}
                </Text>
              </div>
            </Col>
            <Col xs={12} md={16}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={12}>
                  <Statistic
                    title={<span className="text-gray-700 font-semibold">File Size</span>}
                    value={`${(info.fileSize / (1024 * 1024)).toFixed(2)} MB`}
                    prefix={<FileOutlined className="text-blue-500" />}
                    valueStyle={{ fontSize: "1.25rem" }}
                  />
                </Col>
                <Col xs={12} sm={12}>
                  <Statistic
                    title={<span className="text-gray-700 font-semibold">Total Pages</span>}
                    value={info.totalPages || "N/A"}
                    prefix={<ClockCircleOutlined className="text-green-500" />}
                    valueStyle={{ fontSize: "1.25rem" }}
                  />
                </Col>
                <Col xs={12} sm={12}>
                  <Statistic
                    title={<span className="text-gray-700 font-semibold">Est. Word Count</span>}
                    value={info.wordCount?.toLocaleString() || "N/A"}
                    prefix={<FileTextOutlined className="text-purple-500" />}
                    valueStyle={{ fontSize: "1.25rem" }}
                  />
                </Col>
                <Col xs={12} sm={12}>
                  <Statistic
                    title={<span className="text-gray-700 font-semibold">Total Credit Cost</span>}
                    value={costBreakdown.totalCost}
                    suffix=" credits"
                    prefix={<StarOutlined className="text-yellow-500" />}
                    valueStyle={{ fontSize: "1.25rem" }}
                  />
                </Col>
              </Row>
              {renderCostBreakdown(costBreakdown)}
            </Col>
          </Row>
        </Card>
      </motion.div>
    ),
    [
      getFileTypeIcon,
      fileType,
      costBreakdown,
      renderCostBreakdown,
      handleRemoveFile,
      isProcessingDocument,
    ],
  );

  /**
   * Renders the presentation theme selector as a grid of selectable cards.
   * Each card shows a preview image and is clickable to select that theme.
   */
  const renderPresentationThemeSelector = useCallback(
    () => (
      <div className="mt-6">
        <Text strong className="block mb-4 text-gray-700">
          Presentation Theme:
        </Text>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8">
          {presentationThemes.map((theme) => (
            <div
              key={theme.name}
              className={`cursor-pointer border rounded-lg overflow-hidden relative ${
                presentationTheme === theme.name
                  ? "border-indigo-500 shadow-lg"
                  : "border-gray-300 hover:border-indigo-300"
              } transition-transform transform hover:scale-105 p-4`}
              onClick={() => setPresentationTheme(theme.name)}
              role="button"
              aria-pressed={presentationTheme === theme.name}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPresentationTheme(theme.name);
                }
              }}
            >
              <div className="aspect-w-1 aspect-h-1">
                <Image
                  src={theme.imgSrc}
                  alt={theme.label}
                  fill
                  style={{ objectFit: "contain" }}
                  className="rounded-md"
                />
              </div>

              <div className="mt-4 text-center">
                <Text strong className="text-sm sm:text-base">
                  {theme.label}
                </Text>
              </div>

              {presentationTheme === theme.name && (
                <div className="absolute top-2 right-2 bg-indigo-500 text-white rounded-full p-1">
                  <CheckOutlined />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
    [presentationThemes, presentationTheme, setPresentationTheme],
  );

  /**
   * Dragger (file upload) configuration.
   * We disable it while loading document info or processing, and specify allowed file types and size.
   */
  const draggerProps = useMemo(
    () => ({
      name: "file",
      multiple: false,
      accept:
        ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.rtf,.jpg,.jpeg,.png,.mp3,.wav,.m4a,.ogg,.aac,.wma,.flac,.mp4,.mkv,.avi,.mov,.wmv,.webm, .msg, .eml",
      beforeUpload: () => false, // Files are not uploaded automatically, we handle upload manually in `handleFileChange`.
      onChange: handleFileChange,
      showUploadList: false,
      maxSize: 100 * 1024 * 1024, // Custom property (not a standard Dragger prop) indicating max size (100MB).
      disabled: isLoadingDocumentInfo || isProcessingDocument,
    }),
    [handleFileChange, isLoadingDocumentInfo, isProcessingDocument],
  );

  const forceReset = useAppStore((state) => state.forceReset);

  // Function to handle the hotkey
  const handleHotkey = useCallback(
    (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key === "r") {
        event.preventDefault();
        console.log("Hotkey (Ctrl+Alt+R) triggered");
        forceReset();
      }
    },
    [forceReset],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleHotkey);
    return () => window.removeEventListener("keydown", handleHotkey);
  }, [handleHotkey]);

  return (
    <>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}
      <Card className="shadow-lg rounded-lg overflow-hidden mb-4 bg-white hover:shadow-xl transition-shadow">
        <Title
          level={2}
          className="mb-6 text-indigo-800 whitespace-nowrap text-[clamp(1rem,2.5vw,1.5rem)] leading-tight"
        >
          Process New Document
        </Title>
        <Dragger {...draggerProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined
              style={{
                color: isLoadingDocumentInfo ? "#d9d9d9" : "#40a9ff",
              }}
            />
          </p>
          {!isLoadingDocumentInfo ? (
            <>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for PDF, Word, PowerPoint, Excel, audio files (MP3, WAV, etc.), and other
                formats (max {MAX_DOCUMENT_SIZE_MB.toFixed(0)}MB)
              </p>
            </>
          ) : (
            <div className="mt-4 text-center px-8">
              <Spin />
              <Text className="mt-4 block">Analyzing file...</Text>
              <Progress
                percent={uploadProgress}
                size="small"
                className="mt-4 mb-2 max-w-md mx-auto"
                status={uploadProgress === 100 ? "active" : "normal"}
              />
            </div>
          )}
        </Dragger>

        {/* Processing options (quizzes, flashcards, summary, etc.) */}
        <div className="mt-6">
          <Title level={4} className="mb-4 text-indigo-700">
            Processing Options
          </Title>

          <Row gutter={[16, 16]}>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateMultipleChoiceQuiz}
                onChange={(e) => handleOptionChange("multipleChoice", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Generate an interactive multiple-choice quiz to test understanding of the document."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <CheckSquare size={20} color="#2196F3" className="flex-shrink-0" />
                    <span>Generate Multiple Choice Quiz</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateShortAnswerQuiz}
                onChange={(e) => handleOptionChange("shortAnswer", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Generate a short-answer quiz to test in-depth understanding of the document."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <BookOpenText size={20} color="#FFC107" className="flex-shrink-0" />
                    <span>Generate Short Answer Quiz</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateAnkiFlashcards}
                onChange={(e) => handleOptionChange("ankiFlashcards", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Create Anki flashcards for memorization; import into the Anki app."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <Repeat size={20} color="#9C27B0" className="flex-shrink-0" />
                    <span>Generate Anki Flashcards</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generatePresentationSlides}
                onChange={(e) => handleOptionChange("presentationSlides", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Create presentation slides summarizing key points of the document."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <Layers size={20} color="#FF5722" className="flex-shrink-0" />
                    <span>Generate Presentation Slides</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateSummary}
                onChange={(e) => handleOptionChange("summary", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Generate a comprehensive summary of the document."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <FileText size={20} color="#009688" className="flex-shrink-0" />
                    <span>Generate Summary</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateMindmap}
                onChange={(e) => handleOptionChange("mindmap", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Generate a mindmap outlining key concepts and subtopics."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <Brain size={20} color="#3F51B5" className="flex-shrink-0" />
                    <span>Generate Mindmap</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateReadabilityAnalysis}
                onChange={(e) => handleOptionChange("readabilityAnalysis", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Generate a readability analysis with statistics and multiple grade-level versions."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <BookOpenCheck size={20} color="#FF9800" className="flex-shrink-0" />
                    <span>Generate Readability Analysis</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
            <Col xs={12} sm={12}>
              <Checkbox
                checked={generateLessonPlan}
                onChange={(e) => handleOptionChange("lessonPlan", e.target.checked)}
                disabled={isLoadingDocumentInfo || isProcessingDocument || !documentFile}
                className="flex items-center"
              >
                <Tooltip
                  title="Create a structured lesson plan aimed at a specific target audience."
                  mouseEnterDelay={0.5}
                >
                  <div className="flex items-center space-x-2">
                    <GraduationCap size={20} color="#795548" className="flex-shrink-0" />
                    <span>Generate Lesson Plan</span>
                  </div>
                </Tooltip>
              </Checkbox>
            </Col>
          </Row>

          {/* Conditionally render the Presentation Theme selector if presentation slides are chosen */}
          {generatePresentationSlides && renderPresentationThemeSelector()}

          {/* Use the AudienceSelector component if lesson plan is chosen */}
          {generateLessonPlan && (
            <AudienceSelector
              lessonPlanTargetAudience={lessonPlanTargetAudience}
              customLessonPlanTargetAudience={customLessonPlanTargetAudience}
              setLessonPlanTargetAudience={setLessonPlanTargetAudience}
              setCustomLessonPlanTargetAudience={setCustomLessonPlanTargetAudience}
              isLoadingDocumentInfo={isLoadingDocumentInfo}
              isProcessingDocument={isProcessingDocument}
            />
          )}
        </div>

        {/* Buttons for processing and resetting states */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:space-x-4">
          <Button
            type="primary"
            onClick={handleSubmitWrapper}
            loading={isProcessingDocument}
            disabled={!documentFile || isLoadingDocumentInfo}
            className="bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2 w-full sm:w-auto"
            icon={<FileOutlined />}
            size="large"
          >
            {isProcessingDocument ? "Processing..." : "Process File"}
          </Button>

          <AnimatePresence>
            {error || isProcessingDocument ? (
              <motion.div
                key="reset-button"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="w-full sm:w-auto"
              >
                <div className="mt-4">
                  <ResetButton />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </Card>

      {/* Show document info if available */}
      <AnimatePresence mode="wait">
        {documentInfo && !isLoadingDocumentInfo && renderDocumentInfo(documentInfo)}
      </AnimatePresence>
    </>
  );
};

/**
 * A wrapper that places the AddNewDocument component inside the ErrorBoundary.
 * Ensures any runtime errors in AddNewDocument are caught and displayed gracefully.
 */
const AddNewDocumentWithErrorBoundary: React.FC = () => (
  <ErrorBoundary componentName="AddNewDocument">
    <AddNewDocument />
  </ErrorBoundary>
);

export default AddNewDocumentWithErrorBoundary;
